'use client';

import { forwardRef } from 'react';
import { Text } from '../../components/text';
import { IconButton } from '../../components/icon-button';
import { Badge } from '../../components/badge';
import { Tooltip } from '../../components/tooltip';
import {
  Box,
  HStack,
  VStack,
  Divider,
  useBreakpointValue,
} from '@terra/ui-styled-system';
import { LinkSquare02Icon, Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { GitIntegrationPlatformSchema } from '@terra/shared-schemas-git';
import { type CodeGraphWithRelations } from '@terra/shared-schemas-git';
import type { z } from 'zod';
import { generateRepositoryFileUrl } from '../../utils/git-url-generator';
import { TechnologyIcon } from '../technology-icon';

type GitIntegrationPlatformType = z.infer<typeof GitIntegrationPlatformSchema>;

export type RepositoryInfo = {
  platform: GitIntegrationPlatformType;
  owner: string;
  repositoryName: string;
  branch?: string;
  baseUrl?: string | null;
};
export type NodeDetailsPopoverProps = {
  badgeText: string;
  filePath: string;
  startLine?: number;
  endLine?: number;
  repositoryInfo?: RepositoryInfo;
  technologies?: NonNullable<CodeGraphWithRelations['technologies']>;
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
};
export const NodeDetailsPopover = forwardRef<
  HTMLDivElement,
  NodeDetailsPopoverProps
>(
  (
    {
      badgeText,
      filePath,
      startLine,
      endLine,
      repositoryInfo,
      technologies,
      isOpen,
      onClose,
      children,
    },
    ref,
  ) => {
    const t = useTranslations('designSystem.codeGraph.nodeDetailsPopover');
    const rightPosition = useBreakpointValue({
      md: 12,
      lg: 100,
      xl: 250,
    });

    if (!isOpen) return null;
    const handleOpenInRepository = () => {
      if (!repositoryInfo) {
        return;
      }

      const url = generateRepositoryFileUrl({
        platform: repositoryInfo.platform,
        owner: repositoryInfo.owner,
        repositoryName: repositoryInfo.repositoryName,
        filePath,
        branch: repositoryInfo.branch || 'main',
        baseUrl: repositoryInfo.baseUrl,
        startLine,
        endLine,
      });

      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };

    const canOpenInRepository = !!repositoryInfo;

    return (
      <Box
        ref={ref}
        position="absolute"
        style={{ right: `${rightPosition}px` }}
        top="50%"
        transform="translateY(-50%)"
        className="nodrag nowheel"
        cursor="default"
        zIndex={1000}
        bg="neutral.1"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        width="284px"
        boxShadow="0 4px 10px -2px rgba(10, 10, 10, 0.10), 0 2px 5px -2px rgba(10, 10, 10, 0.05)"
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.stopPropagation()}
      >
        <VStack gap={0} alignItems="flex-start">
          <Box p={3} pb={4} w="full">
            <HStack justify="space-between" alignItems="center" w="full">
              <Text fontSize="sm" fontWeight="medium" color="fg.subtle">
                {t('title')}
              </Text>
              <IconButton
                size="xs"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                aria-label="Close"
              >
                <Cancel01Icon size={16} />
              </IconButton>
            </HStack>
            <Badge size="sm" variant="fill">
              {badgeText}
            </Badge>
          </Box>

          <Divider borderColor="border.subtle" />

          <Box p={4} w="full" maxH="468px" overflowY="auto">
            <VStack gap={6} alignItems="flex-start">
              <VStack gap={2} alignItems="flex-start" w="full">
                <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                  {t('filePath')}
                </Text>
                <Box
                  p={2}
                  bg="bg.subtle"
                  borderWidth="1px"
                  borderColor="border.subtle"
                  borderRadius="sm"
                  w="full"
                >
                  <HStack
                    justify="space-between"
                    alignItems="flex-start"
                    gap={2}
                  >
                    <Text
                      fontSize="xs"
                      wordBreak="break-all"
                      flex={1}
                      whiteSpace="normal"
                    >
                      {filePath}
                    </Text>
                    {canOpenInRepository && (
                      <Tooltip
                        title={t('openInRepository')}
                        positioning={{ placement: 'top' }}
                      >
                        <IconButton
                          size="xs"
                          variant="ghost"
                          onClick={handleOpenInRepository}
                          aria-label="Open in repository"
                          flexShrink={0}
                        >
                          <LinkSquare02Icon
                            size={16}
                            color="var(--colors-fg-muted)"
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </HStack>
                </Box>
              </VStack>

              {technologies && technologies.length > 0 && (
                <VStack gap={2} alignItems="flex-start" w="full">
                  <Text fontSize="xs" color="fg.subtle" fontWeight="medium">
                    {`${t('technologies')} (${technologies.length})`}
                  </Text>
                  <VStack gap={2} alignItems="flex-start" w="full">
                    {technologies.map((technology, index) => {
                      return (
                        <Box
                          key={index}
                          p={2}
                          borderWidth="1px"
                          borderColor="border.subtle"
                          borderRadius="sm"
                          bg="bg.subtle"
                          w="full"
                        >
                          <VStack gap={2.5} alignItems="flex-start" w="full">
                            <HStack gap={2} alignItems="center">
                              <TechnologyIcon
                                name={technology.name}
                                style={{ width: 16, height: 16 }}
                              />
                              <Text fontSize="sm">{technology.name}</Text>
                            </HStack>
                            {technology.version && (
                              <Text fontSize="xs" color="fg.muted">
                                {t('version')}: {technology.version}
                              </Text>
                            )}
                          </VStack>
                        </Box>
                      );
                    })}
                  </VStack>
                </VStack>
              )}
            </VStack>
          </Box>

          {children}
        </VStack>
      </Box>
    );
  },
);
NodeDetailsPopover.displayName = 'NodeDetailsPopover';
